/**
 * HmContractors — kartoteka kontrahentów czytana z Symfonii (TYLKO PODGLĄD).
 *
 * To NIE są nasi kontrahenci z bazy tenanta i te dwa zbiory świadomie się nie mieszają: niczego
 * nie synchronizujemy, nie kopiujemy i nie edytujemy. Dokument powstaje w Symfonii i wskazuje
 * kontrahenta z JEJ kartoteki, więc to ona jest tu źródłem prawdy — dokładnie jak przy towarach.
 *
 * Bez dat, jak towary: kontrahenci nie mają zakresu czasowego, więc lewy panel dat jest wyłączony,
 * a całość pobiera się jednym zapytaniem. Grupujemy po stanie (aktywni / nieaktywni), bo to
 * jedyny podział, który Symfonia podaje na liście.
 *
 * PESEL nie jest pobierany ani pokazywany, choć Symfonia go oddaje (decyzja właściciela
 * 2026-09-14) — do podglądu handlowego nie jest potrzebny.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HmContractorListItem } from '@saas/shared';
import { Users } from 'lucide-react';

import type { ColumnDefinition } from '@/components/configurable-table';
import { HmListShell } from '@/components/handel-magazyn/HmListShell';

export function HmContractors() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo<ColumnDefinition<HmContractorListItem>[]>(
    () => [
      {
        id: 'code',
        header: t('tenant.handelMagazyn.contractors.columns.code'),
        accessor: (row) => row.code,
        defaultWidth: 180,
        renderCell: (_v: unknown, row: HmContractorListItem) => (
          <span className="flex items-center gap-2">
            {/* Kropka stanu jak na dokumentach — nieaktywny kontrahent nadal bywa na starych
                dokumentach, więc musi być widoczny, ale odróżnialny na pierwszy rzut oka. */}
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${row.active ? 'bg-emerald-500' : 'bg-text-tertiary'}`}
            />
            <span className="font-mono text-xs text-text-primary">{row.code}</span>
          </span>
        ),
      },
      {
        id: 'name',
        header: t('tenant.handelMagazyn.contractors.columns.name'),
        accessor: (row) => row.name,
        defaultWidth: 320,
      },
      {
        id: 'taxId',
        header: t('tenant.handelMagazyn.contractors.columns.taxId'),
        accessor: (row) => row.taxId ?? '—',
        defaultWidth: 150,
        renderCell: (_v: unknown, row: HmContractorListItem) =>
          row.taxId ? (
            <span className="font-mono text-xs text-text-primary">{row.taxId}</span>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      },
      {
        id: 'city',
        header: t('tenant.handelMagazyn.contractors.columns.city'),
        accessor: (row) => row.city ?? '—',
        defaultWidth: 180,
      },
      {
        id: 'postCode',
        header: t('tenant.handelMagazyn.contractors.columns.postCode'),
        accessor: (row) => row.postCode ?? '—',
        defaultWidth: 110,
      },
      {
        id: 'active',
        header: t('tenant.handelMagazyn.columns.status'),
        accessor: (row) =>
          row.active
            ? t('tenant.handelMagazyn.contractors.active')
            : t('tenant.handelMagazyn.contractors.inactive'),
        defaultWidth: 120,
      },
    ],
    [t],
  );

  return (
    <HmListShell<HmContractorListItem>
      title={t('tenant.handelMagazyn.contractors.title')}
      resource="contractors"
      queryKey="contractors"
      tableId="hm_contractors"
      testIdPrefix="hm-contractors"
      columns={columns}
      getRowId={(row) => String(row.id)}
      onNew={() => navigate('/handel-magazyn/kontrahenci/nowy')}
      onRowClick={(row) => navigate(`/handel-magazyn/kontrahenci/${row.id}`)}
      emptyIcon={Users}
      showDateFilter={false}
      groupBy={{
        label: t('tenant.handelMagazyn.columns.status'),
        of: (row) =>
          row.active
            ? t('tenant.handelMagazyn.contractors.active')
            : t('tenant.handelMagazyn.contractors.inactive'),
      }}
    />
  );
}

export default HmContractors;
